import { useMemo, useState } from "react";

/**
 * JSON 只读视图（原型 `highlightJSON` 2506 / Wiki D-02）。
 *
 * 原型用正则往 `innerHTML` 里塞 `<span>`；这里改成**先切 token 再渲染 React 节点**，
 * 不碰 `dangerouslySetInnerHTML`，值里带 `<` `&` 也不会破结构。
 * 颜色走 token：键 = 琥珀、字符串 = 玉、数字 = 蓝、true/false/null = 红。
 */

export type JsonTokenType = "key" | "string" | "number" | "literal" | "punct" | "plain";

export interface JsonToken {
  type: JsonTokenType;
  value: string;
}

const TOKEN_RE =
  /("(?:\\.|[^"\\])*")(\s*:)?|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)|(true|false|null)|([{}[\],:])/g;

/** 把 JSON 文本切成带类型的 token（纯函数，单测直接打）。 */
export function tokenizeJson(json: string): JsonToken[] {
  const tokens: JsonToken[] = [];
  let cursor = 0;
  TOKEN_RE.lastIndex = 0;

  const pushPlain = (value: string) => {
    if (value.length === 0) return;
    const last = tokens[tokens.length - 1];
    if (last && last.type === "plain") last.value += value;
    else tokens.push({ type: "plain", value });
  };

  let match: RegExpExecArray | null = TOKEN_RE.exec(json);
  while (match !== null) {
    if (match.index > cursor) pushPlain(json.slice(cursor, match.index));
    const [raw, str, colon, num, literal, punct] = match;
    if (str !== undefined) {
      tokens.push({ type: colon ? "key" : "string", value: str });
      if (colon) pushPlain(colon);
    } else if (num !== undefined) {
      tokens.push({ type: "number", value: num });
    } else if (literal !== undefined) {
      tokens.push({ type: "literal", value: literal });
    } else if (punct !== undefined) {
      tokens.push({ type: "punct", value: punct });
    } else {
      pushPlain(raw);
    }
    cursor = match.index + raw.length;
    match = TOKEN_RE.exec(json);
  }
  if (cursor < json.length) pushPlain(json.slice(cursor));
  return tokens;
}

export interface JsonViewProps {
  /** 任意可序列化负载；序列化失败时给一行错误，不炸面板。 */
  data: unknown;
  /** 复制按钮的可见文案前缀（默认「复制」）。 */
  copyLabel?: string;
  className?: string;
}

function serialize(data: unknown): string {
  try {
    return JSON.stringify(data, null, 2) ?? "null";
  } catch {
    return '"（这份数据无法序列化）"';
  }
}

export function JsonView({ data, copyLabel = "复制", className }: JsonViewProps) {
  const json = useMemo(() => serialize(data), [data]);
  const tokens = useMemo(() => tokenizeJson(json), [json]);
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard?.writeText(json);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch {
      // 剪贴板不可用（无权限 / 非安全上下文）：不弹错，用户可手动选中。
      setCopied(false);
    }
  }

  return (
    <div className={`dp-json-wrap${className ? ` ${className}` : ""}`}>
      <div className="dp-json-bar">
        <span className="dp-json-len" aria-hidden="true">
          {json.length} 字符
        </span>
        <span className="grow" />
        <button type="button" className="dp-btn sm" onClick={() => void copy()}>
          {copied ? "已复制" : copyLabel}
        </button>
      </div>
      <pre className="dp-json" aria-label="JSON 数据" tabIndex={0}>
        {tokens.map((token, index) =>
          token.type === "plain" ? (
            <span key={index}>{token.value}</span>
          ) : (
            <span key={index} className={`dp-json-${token.type}`}>
              {token.value}
            </span>
          ),
        )}
      </pre>
    </div>
  );
}
